import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import { UUID, CreateExtensionRequest, DocumentExtensionSchema, ExtensionConceptSummary, UpdateExtensionRequest } from './types';
import { taxonomyQueryKeys } from './taxonomy';
import { dispatchMessage, EventType } from 'app/useToasts';
import { i18n } from 'app/utils/i18n';

export const extensionQueryKeys = {
  extensions: 'EXTENSIONS',
  schema: 'EXTENSION_SCHEMA',
} as const;

export function useExtensions(documentSuperId: number | undefined, taxonomyId: UUID | undefined): UseQueryResult<ExtensionConceptSummary[]> {
  return useQuery(
    [extensionQueryKeys.extensions, documentSuperId, taxonomyId],
    async () => {
      const res = await request.get(`/gaby/concepts/extensions?documentSuperId=${documentSuperId}&taxonomyId=${taxonomyId}`);
      return (res?.data as ExtensionConceptSummary[]) || [];
    },
    {
      enabled: !!documentSuperId && !!taxonomyId,
      onError: ServerErrorHandler,
    }
  );
}

export function useDocumentExtensionSchema(documentSuperId: number | undefined): UseQueryResult<DocumentExtensionSchema> {
  return useQuery(
    [extensionQueryKeys.schema, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/document-taxonomy-config/${documentSuperId}/extension-schema`);
      return (res?.data as DocumentExtensionSchema) || { prefix: null, namespace: null };
    },
    {
      enabled: !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export interface UpdateExtensionSchemaInput {
  documentSuperId: number;
  prefix: string;
  namespace: string;
}

export function useUpdateDocumentExtensionSchema(): UseMutationResult<DocumentExtensionSchema, unknown, UpdateExtensionSchemaInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentSuperId, prefix, namespace }: UpdateExtensionSchemaInput) => {
      const res = await request.put(`/gaby/document-taxonomy-config/${documentSuperId}/extension-schema`, {
        prefix,
        namespace,
      });
      return res?.data as DocumentExtensionSchema;
    },
    {
      onError: (error: any) => {
        let message = i18n('tag.extension.schema.update.error');
        try {
          const body = JSON.parse(error.request?.responseText);
          if (body?.error) message = body.error;
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag.extension.schema.update.success'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([extensionQueryKeys.schema, variables.documentSuperId]);
      },
    }
  );
}

export function useCreateExtension(): UseMutationResult<ExtensionConceptSummary, unknown, CreateExtensionRequest> {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: CreateExtensionRequest) => {
      const res = await request.post(`/gaby/concepts/extensions`, input);
      return res?.data as ExtensionConceptSummary;
    },
    {
      onError: (error: any) => {
        let message = i18n('tag.extension.create.error');
        try {
          const body = JSON.parse(error.request?.responseText);
          if (body?.error) message = body.error;
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag.extension.create.success'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId]);
      },
    }
  );
}

export function useUpdateExtension(): UseMutationResult<{ conceptId: UUID }, unknown, UpdateExtensionRequest> {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: UpdateExtensionRequest) => {
      const res = await request.put(`/gaby/concepts/extensions/${input.conceptId}?documentSuperId=${input.documentSuperId}`, {
        name: input.name,
        parentMemberConceptId: input.parentMemberConceptId,
        description: input.description,
        labels: input.labels,
        widerAnchorConceptId: input.widerAnchorConceptId,
        narrowerAnchorConceptIds: input.narrowerAnchorConceptIds,
        conceptType: input.conceptType,
        summands: input.summands,
      });
      return res?.data as { conceptId: UUID };
    },
    {
      onError: (error: any) => {
        let message = i18n('tag.extension.update.error');
        try {
          const body = JSON.parse(error.request?.responseText);
          if (body?.error) message = body.error;
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag.extension.update.success'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId]);
        queryClient.invalidateQueries([taxonomyQueryKeys.dimensionsForConcept]);
      },
    }
  );
}

export interface CreateExtensionMemberRequest {
  documentSuperId: number;
  taxonomyId: UUID;
  name: string;
  labels: Record<string, Record<string, string>>;
  axisConceptId: UUID;
  parentMemberConceptId: UUID;
  anchorMemberConceptId?: UUID;
  description?: string | null;
}

export function useCreateExtensionMember(): UseMutationResult<{ conceptId: UUID; name: string }, unknown, CreateExtensionMemberRequest> {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: CreateExtensionMemberRequest) => {
      const res = await request.post(`/gaby/concepts/extensions`, { ...input, extensionShape: 'MEMBER' });
      return res?.data as { conceptId: UUID; name: string };
    },
    {
      onError: (error: any) => {
        let message = i18n('tag.extension.create.error');
        try {
          const body = JSON.parse(error.request?.responseText);
          if (body?.error) message = body.error;
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag.extension.create.success'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId]);
        queryClient.invalidateQueries([taxonomyQueryKeys.dimensionsForConcept]);
      },
    }
  );
}

export interface DeleteExtensionInput {
  extensionId: UUID;
  documentSuperId: number;
  taxonomyId: UUID;
}

export function useDeleteExtension(): UseMutationResult<unknown, unknown, DeleteExtensionInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ extensionId, documentSuperId }: DeleteExtensionInput) => {
      const res = await request.del(`/gaby/concepts/extensions/${extensionId}?documentSuperId=${documentSuperId}`);
      return res?.data;
    },
    {
      onError: (error: any) => {
        let message = i18n('tag.extension.delete.error');
        try {
          const body = JSON.parse(error.request?.responseText);
          if (body?.error) message = body.error;
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 4000 });
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag.extension.delete.success'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([extensionQueryKeys.extensions, variables.documentSuperId, variables.taxonomyId]);
        queryClient.invalidateQueries([taxonomyQueryKeys.dimensionsForConcept]);
      },
    }
  );
}
